import { useEffect, useRef, useState, lazy, Suspense } from 'react';
import { Routes, Route, useLocation } from 'react-router-dom';
import './styles/global.css';
import { useApp } from './context.jsx';
import Nav from './components/Nav.jsx';
import Footer from './components/Footer.jsx';
import { trackUtmIdea } from './lib/utm.js';

// Home eager — нужен для первого paint'а после curtain intro.
// Остальные роуты split'ятся по запросу.
import Home            from './routes/Home.jsx';

const Artworks        = lazy(() => import('./routes/Artworks.jsx'));
const ArtworkDetail   = lazy(() => import('./routes/ArtworkDetail.jsx'));
const Prints          = lazy(() => import('./routes/Prints.jsx'));
const Workshops       = lazy(() => import('./routes/Workshops.jsx'));
const Custom          = lazy(() => import('./routes/Custom.jsx'));
const Collaborations  = lazy(() => import('./routes/Collaborations.jsx'));
const About           = lazy(() => import('./routes/About.jsx'));
const Contact         = lazy(() => import('./routes/Contact.jsx'));
const Blog            = lazy(() => import('./routes/Blog.jsx'));
const BlogPost        = lazy(() => import('./routes/BlogPost.jsx'));
const AdminHub        = lazy(() => import('./routes/AdminHub.jsx'));
const Academy         = lazy(() => import('./routes/Academy.jsx'));
const CourseDetail    = lazy(() => import('./routes/CourseDetail.jsx'));
const Privacy         = lazy(() => import('./routes/Privacy.jsx'));
const Constellation   = lazy(() => import('./routes/Constellation.jsx'));
const Lab             = lazy(() => import('./routes/Lab.jsx'));
const NotFound        = lazy(() => import('./routes/NotFound.jsx'));

const RouteFallback = () => (
  <div style={{ minHeight: '60vh' }} aria-hidden="true" />
);

export default function App() {
  const { lang, t, cursorMode, toast } = useApp();
  const cursorRef = useRef(null);
  const cursorRingRef = useRef(null);
  const scrollProgRef = useRef(null);
  const topBtnRef = useRef(null);
  const mousePos = useRef({ x: 0, y: 0 });
  const ringPos = useRef({ x: 0, y: 0 });
  const location = useLocation();
  const [curtain, setCurtain] = useState(true);
  const [hp, setHp] = useState(0);

  // curtain intro
  useEffect(() => {
    const timers = [
      setTimeout(() => setHp(1), 300),
      setTimeout(() => setCurtain(false), 1000),
    ];
    return () => timers.forEach(clearTimeout);
  }, []);

  // AI-Office UTM-pixel — fire once on first paint if ?utm_idea=<uuid>
  useEffect(() => { trackUtmIdea(); }, []);

  // scroll-to-top при смене маршрута
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [location.pathname]);

  // document title
  useEffect(() => {
    const base = lang === 'ru' ? 'Елизавета Федькина' : 'Elizaveta Fedkina';
    const suffix = lang === 'ru' ? 'Интуитивная живопись' : 'Intuitive Painting';
    const labels = t.nav2 || {};
    const titles = {
      '/':               `${base} — ${suffix}`,
      '/artworks':       `${labels.artworks || 'Artworks'} — ${base}`,
      '/prints':         `${labels.prints || 'Prints'} — ${base}`,
      '/workshops':      `${labels.workshops || 'Workshops'} — ${base}`,
      '/custom':         `${labels.custom || 'Custom'} — ${base}`,
      '/collaborations': `${labels.collabs || 'Collaborations'} — ${base}`,
      '/about':          `${labels.about || 'About'} — ${base}`,
      '/blog':           `${labels.blog || 'Blog'} — ${base}`,
      '/contact':        `${labels.contact || 'Contact'} — ${base}`,
    };
    document.title = titles[location.pathname] || `${base} — ${suffix}`;
  }, [lang, location.pathname, t.nav2]);

  // cursor trailing
  useEffect(() => {
    let raf;
    const lerp = (a, b, t) => a + (b - a) * t;
    const tick = () => {
      ringPos.current.x = lerp(ringPos.current.x, mousePos.current.x, 0.12);
      ringPos.current.y = lerp(ringPos.current.y, mousePos.current.y, 0.12);
      if (cursorRingRef.current) {
        const s = cursorRingRef.current.offsetWidth / 2;
        cursorRingRef.current.style.transform = `translate(${ringPos.current.x - s}px, ${ringPos.current.y - s}px)`;
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, []);

  useEffect(() => {
    const onScroll = () => {
      const d = document.documentElement;
      const pct = d.scrollTop / (d.scrollHeight - d.clientHeight || 1);
      if (scrollProgRef.current) scrollProgRef.current.style.width = `${pct * 100}%`;
      if (topBtnRef.current) {
        topBtnRef.current.style.opacity = window.scrollY > 600 ? '1' : '0';
        topBtnRef.current.style.pointerEvents = window.scrollY > 600 ? 'auto' : 'none';
      }
    };
    const onMouse = (e) => {
      mousePos.current = { x: e.clientX, y: e.clientY };
      if (cursorRef.current) cursorRef.current.style.transform = `translate(${e.clientX - 4}px, ${e.clientY - 4}px)`;
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('mousemove', onMouse);
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('mousemove', onMouse);
    };
  }, []);

  return (
    <>
      <a className="skip-link" href="#main">{lang === 'ru' ? 'К содержанию' : 'Skip to content'}</a>
      {curtain && (
        <div className={`curtain ${hp >= 1 ? 'open' : ''}`} aria-hidden="true">
          <div className="curtain-logo">{lang === 'ru' ? 'ЕЛИЗАВЕТА ФЕДЬКИНА' : 'ELIZAVETA FEDKINA'}</div>
          <div className="curtain-l" /><div className="curtain-r" />
        </div>
      )}
      <div ref={cursorRef} className={`cursor cursor--${cursorMode}`} style={{ left: 0, top: 0, willChange: 'transform' }}>
        <div className="cursor-dot" />
      </div>
      <div ref={cursorRingRef} className={`cursor-ring cursor-ring--${cursorMode}`} style={{ left: 0, top: 0, willChange: 'transform' }}>
        <span className="cursor-ring-text">{cursorMode === 'view' ? 'View' : ''}</span>
      </div>
      <div ref={scrollProgRef} className="scroll-prog" />
      <div className="noise" />

      <Nav />

      <main id="main">
        <Suspense fallback={<RouteFallback />}>
          <Routes>
            <Route path="/"               element={<Home />} />
            <Route path="/artworks"       element={<Artworks />} />
            <Route path="/artworks/:slug" element={<ArtworkDetail />} />
            <Route path="/prints"         element={<Prints />} />
            <Route path="/workshops"      element={<Workshops />} />
            <Route path="/custom"         element={<Custom />} />
            <Route path="/collaborations" element={<Collaborations />} />
            <Route path="/about"          element={<About />} />
            <Route path="/blog"           element={<Blog />} />
            <Route path="/blog/:slug"     element={<BlogPost />} />
            <Route path="/contact"        element={<Contact />} />
            <Route path="/admin/*"        element={<AdminHub />} />
            <Route path="/academy"        element={<Academy />} />
            <Route path="/academy/:slug"  element={<CourseDetail />} />
            <Route path="/privacy"        element={<Privacy />} />
            <Route path="/constellation"  element={<Constellation />} />
            <Route path="/lab"            element={<Lab />} />
            <Route path="*"               element={<NotFound />} />
          </Routes>
        </Suspense>
      </main>

      <Footer />

      <button
        ref={topBtnRef}
        className="top-btn"
        onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
        aria-label="Scroll to top"
      >↑</button>
      {toast && <div className="toast">{toast}</div>}
    </>
  );
}
